import React from 'react';
import { ParameterCard } from './SharedComponents';
import './CyberPanel.css'; // We'll create this CSS file


export const WaterPanel = ({ parameters }) => {
  const waterParams = [
    { name: 'Temperature', value: parameters.temperature, unit: '°C', min: 20, max: 30 },
    { name: 'Salinity', value: parameters.salinity, unit: 'ppt', min: 30, max: 40 },
    { name: 'pH', value: parameters.ph, unit: '', min: 7.5, max: 8.5 },
    { name: 'Flow Rate', value: parameters.flowRate, unit: 'cm/s', min: 0, max: 5 }
  ];

  return (
    <div className="cyber-panel neon-blue">
      <h2 className="panel-title cyber-glitch">WATER CHEMISTRY</h2>
      <div className="parameter-grid cyber-grid">
        {waterParams.map(param => (
          <ParameterCard 
            key={param.name}
            name={param.name}
            value={`${param.value}${param.unit}`}
            min={param.min}
            max={param.max}
            current={parseFloat(param.value)}
            unit={param.unit}
            cyberStyle={true}
          />
        ))}
      </div>
      <div className="cyber-corner cyber-corner-tl"></div>
      <div className="cyber-corner cyber-corner-tr"></div>
      <div className="cyber-corner cyber-corner-bl"></div>
      <div className="cyber-corner cyber-corner-br"></div>
    </div>
  );
};



// Water Chemistry Panel
export const WaterChemistryPanel = ({ parameters }) => (
  <div className="panel">
    <h2>Water Chemistry</h2>
    <div className="parameter-grid">
      <ParameterCard 
        name="Temperature" 
        value={`${parameters.temperature}°C`} 
        min={20} max={30} 
        current={parseFloat(parameters.temperature)}
        unit="°C"
      />
      <ParameterCard 
        name="Salinity" 
        value={`${parameters.salinity} ppt`} 
        min={30} max={40} 
        current={parseFloat(parameters.salinity)}
        unit="ppt"
      />
      <ParameterCard 
        name="pH" 
        value={parameters.ph} 
        min={7.5} max={8.5} 
        current={parseFloat(parameters.ph)}
        unit=""
      />
    </div>
  </div>
);

// Gases Panel
export const GasesPanel = ({ parameters }) => (
  <div className="panel">
    <h2>Dissolved Gases</h2>
    <div className="parameter-grid">
      <ParameterCard 
        name="Oxygen (O₂)" 
        value={`${parameters.oxygen} mg/L`} 
        min={5} max={8} 
        current={parseFloat(parameters.oxygen)}
        unit="mg/L"
      />
      <ParameterCard 
        name="Carbon Dioxide (CO₂)" 
        value={`${parameters.co2} ppm`} 
        min={300} max={500} 
        current={parseFloat(parameters.co2)}
        unit="ppm"
      />
    </div>
  </div>
);






export const LightingPanel = ({ 
  parameters, 
  onParameterChange,
  lightSpectrum, 
  setLightSpectrum 
}) => {
  // Helper function to adjust color brightness
  const adjustBrightness = (color, factor) => {
    const r = parseInt(color.substr(1, 2), 16);
    const g = parseInt(color.substr(3, 2), 16);
    const b = parseInt(color.substr(5, 2), 16);
    const adjusted = (val) => Math.min(255, Math.floor(val * factor));
    return `#${adjusted(r).toString(16).padStart(2, '0')}${adjusted(g).toString(16).padStart(2, '0')}${adjusted(b).toString(16).padStart(2, '0')}`;
  };

  const spectrumOptions = [
    { 
      id: 'coral-growth', 
      name: 'Coral',
      label: 'Coral (400-500nm)',
      colors: ['#FF7F50', '#FF4500', '#FF6347', '#FFA07A'],
      icon: '🪸',
      wavelength: '400-500nm (Blue/UV)'
    },
    { 
      id: 'phytoplankton', 
      name: 'Plankton',
      label: 'Plankton (450-550nm)',
      colors: ['#20B2AA', '#008B8B', '#00CED1', '#48D1CC'],
      icon: '🦠',
      wavelength: '450-550nm (Blue/Green)'
    },
    { 
      id: 'full-spectrum', 
      name: 'Full',
      label: 'Full (400-700nm)',
      colors: ['#9400D3', '#4B0082', '#0000FF', '#00FF00', '#FFFF00', '#FF7F00', '#FF0000'],
      icon: '🌈',
      wavelength: '400-700nm (Full Spectrum)'
    },
    { 
      id: 'moonlight', 
      name: 'Moon',
      label: 'Moon (380-450nm)',
      colors: ['#E6E6FA', '#D8BFD8', '#DDA0DD', '#EE82EE'],
      icon: '🌙',
      wavelength: '380-450nm (Violet)'
    }
  ];

  const activeSpectrum = spectrumOptions.find(s => s.id === lightSpectrum) || spectrumOptions[0];
  const brightness = 0.2 + (parameters.lightIntensity / 100 * 0.8);

  return (
    <div className="cyber-panel neon-pink compact-light-panel">
      <h2 className="panel-title cyber-glitch">LIGHTING CONTROL</h2>
      <div className="lighting-controls-compact">
        <div className="light-sphere-container">
          <div 
            className="light-sphere"
            style={{
              background: `radial-gradient(circle at 30% 30%, 
                ${activeSpectrum.colors.map((c, i) => 
                  `${adjustBrightness(c, brightness)} ${i * (100/activeSpectrum.colors.length)}%`
                ).join(', ')})`,
              boxShadow: `
                0 0 ${20 * brightness}px ${activeSpectrum.colors[0]},
                inset -15px -15px 30px rgba(0, 0, 0, ${0.7 - (brightness * 0.5)}),
                inset 8px 8px 15px rgba(255, 255, 255, ${0.2 * brightness})
              `,
              opacity: brightness,
              transform: `scale(${0.8 + (brightness * 0.4)})`
            }}
          >
            <div className="light-sphere-highlight"></div>
            <div className="light-sphere-icon">{activeSpectrum.icon}</div>
          </div>
          <div className="spectrum-info">
            <div className="spectrum-name">{activeSpectrum.label}</div>
            <div className="wavelength">{activeSpectrum.wavelength}</div>
          </div>
          <div className="intensity-slider-compact">
            <input 
              type="range"
              min="0"
              max="100"
              value={parameters.lightIntensity}
              onChange={(e) => onParameterChange('lightIntensity', e.target.value)}
              className="cyber-range"
            />
            <div className="slider-value neon-text">{parameters.lightIntensity}%</div>
          </div>
        </div>

        <div className="mode-selection-compact">
          <div className="mode-buttons-grid">
            {spectrumOptions.map(spectrum => (
              <button
              key={spectrum.id}
              className={`mode-button-compact ${lightSpectrum === spectrum.id ? 'active' : ''}`}
              onClick={() => setLightSpectrum(spectrum.id)}
           
            
              >
                <span className="mode-icon">{spectrum.icon}</span>
                <span className="mode-label">{spectrum.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="cyber-corner cyber-corner-tl"></div>
      <div className="cyber-corner cyber-corner-tr"></div>
      <div className="cyber-corner cyber-corner-bl"></div>
      <div className="cyber-corner cyber-corner-br"></div>
    </div>
  );
};

export const AIRecommendationPanel = ({ recommendation, onDose, vitalityIndex, selectedOrganism }) => {
  return (
    <div className="cyber-panel neon-green">
      <h2 className="panel-title cyber-glitch">AI RECOMMENDATIONS</h2>
      <div className="ai-recommendation cyber-text">
        {recommendation}
      </div>
      <div className="cyber-corner cyber-corner-tl"></div>
      <div className="cyber-corner cyber-corner-tr"></div>
      <div className="cyber-corner cyber-corner-bl"></div>
      <div className="cyber-corner cyber-corner-br"></div>
    </div>
  );
};

export const HealthPanel = ({ vitalityIndex, selectedOrganism, selectedEcosystem }) => {
  if (!selectedEcosystem) {
    return (
      <div className="cyber-panel neon-orange">
        <p className="cyber-text">SELECT ECOSYSTEM TO VIEW DETAILS</p>
        <div className="cyber-corner cyber-corner-tl"></div>
        <div className="cyber-corner cyber-corner-tr"></div>
        <div className="cyber-corner cyber-corner-bl"></div>
        <div className="cyber-corner cyber-corner-br"></div>
      </div>
    );
  }
  
  const organismName = selectedOrganism?.name || "NO ORGANISM SELECTED";
  const health = selectedOrganism?.health || vitalityIndex;
  const growthRate = selectedOrganism?.growthRate || Math.min(100, vitalityIndex + 10);
  const vitality = selectedOrganism?.vitality || vitalityIndex;
  const description = selectedOrganism?.description || "";

  return (
    <div className="cyber-panel neon-orange">
      <h3 className="panel-title cyber-glitch">ORGANISM VITALITY INDEX</h3>
      <div className="vitality-bar cyber-bar">
        <div 
          className="vitality-fill cyber-fill"
          style={{ 
            width: `${vitalityIndex}%`,
            background: `linear-gradient(90deg, ${getVitalityColor(vitalityIndex)}, #00ffff)`,
            boxShadow: `0 0 10px ${getVitalityColor(vitalityIndex)}`
          }} 
        />
        <span className="neon-text">{vitalityIndex}%</span>
      </div>

      {selectedOrganism && (
        <div className="organism-analysis cyber-analysis">
          <h3 className="cyber-subtitle">ORGANISM ANALYSIS: {organismName}</h3>
          
          <div className="health-metrics cyber-metrics">
            <div className="metric cyber-metric">
              <h4>HEALTH</h4>
              <div className="metric-bar cyber-bar">
                <div 
                  className="metric-fill cyber-fill"
                  style={{ 
                    width: `${health}%`,
                    background: `linear-gradient(90deg, #00ff00, #00ffff)`,
                    boxShadow: '0 0 10px #00ff00'
                  }}
                />
                <span className="neon-text">{health}%</span>
              </div>
            </div>
            
            <div className="metric cyber-metric">
              <h4>GROWTH RATE</h4>
              <div className="metric-bar cyber-bar">
                <div 
                  className="metric-fill cyber-fill"
                  style={{ 
                    width: `${growthRate}%`,
                    background: `linear-gradient(90deg, #ff00ff, #00ffff)`,
                    boxShadow: '0 0 10px #ff00ff'
                  }}
                />
                <span className="neon-text">{growthRate}%</span>
              </div>
            </div>
            
            <div className="metric cyber-metric">
              <h4>VITALITY</h4>
              <div className="metric-bar cyber-bar">
                <div 
                  className="metric-fill cyber-fill"
                  style={{ 
                    width: `${vitality}%`,
                    background: `linear-gradient(90deg, #ff5500, #ffff00)`,
                    boxShadow: '0 0 10px #ff5500'
                  }}
                />
                <span className="neon-text">{vitality}%</span>
              </div>
            </div>
          </div>
          
          {description && (
            <div className="organism-description cyber-description">
              <h4 className="cyber-subtitle">DESCRIPTION</h4>
              <p className="cyber-text">{description}</p>
            </div>
          )}
        </div>
      )}
      <div className="cyber-corner cyber-corner-tl"></div>
      <div className="cyber-corner cyber-corner-tr"></div>
      <div className="cyber-corner cyber-corner-bl"></div>
      <div className="cyber-corner cyber-corner-br"></div>
    </div>
  );
};

// Enhanced vitality color with neon effect
const getVitalityColor = (value) => {
  if (value >= 75) return '#00ff00';  // Neon Green
  if (value >= 50) return '#ffff00';  // Neon Yellow
  if (value >= 25) return '#ff5500';  // Neon Orange
  return '#ff0000';                   // Neon Red
};